import React, { useState, useEffect, useRef, useCallback } from "react";
import { Play, Pause, RotateCcw, Volume2, Repeat } from "lucide-react";
import { useGuitarSynth } from "../../hooks/useGuitarSynth";
import { getChordMidiNotes } from "../../lib/musicTheory";
import { getFunctionColor } from "./ProgressionSequencer";
import { type NoteName } from "../../lib/guitarEngine";

export interface ProgressionPlayerProps {
  progression: string[];
  keySignature?: NoteName;
  bpm: number;
  onBpmChange: (bpm: number) => void;
  isPlaying: boolean;
  onTogglePlay: (playing: boolean) => void;
  activeIndex: number;
  onActiveIndexChange: (index: number) => void;
}

const BAR_COUNT = 12;

export const ProgressionPlayer: React.FC<ProgressionPlayerProps> = ({
  progression,
  keySignature = "C",
  bpm,
  onBpmChange,
  isPlaying,
  onTogglePlay,
  activeIndex,
  onActiveIndexChange,
}) => {
  const { playNote } = useGuitarSynth();
  const [loop, setLoop] = useState(true);
  const [volume, setVolume] = useState(0.25);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const cancelledRef = useRef(false);

  const beatDurationMs = (60 / bpm) * 1000;
  const measureDurationMs = beatDurationMs * 4; // 4/4 por defecto

  // Play arpeggio for a single chord
  const playArpeggio = useCallback(
    (chordName: string) => {
      const notes = getChordMidiNotes(chordName);
      if (notes.length === 0) return;

      const noteDuration = Math.min(400, measureDurationMs / notes.length);
      notes.forEach((n, i) => {
        setTimeout(() => {
          if (!cancelledRef.current) {
            playNote(n.note, n.octave, noteDuration);
          }
        }, i * 120);
      });
    },
    [playNote, measureDurationMs]
  );

  // Playback loop
  useEffect(() => {
    if (!isPlaying) {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      cancelledRef.current = true;
      return;
    }

    cancelledRef.current = false;
    const validProgression = progression.filter((c) => c.trim() !== "");
    if (validProgression.length === 0) {
      onTogglePlay(false);
      return;
    }

    let idx = activeIndex >= 0 ? activeIndex : 0;
    onActiveIndexChange(idx);
    playArpeggio(validProgression[idx]);

    intervalRef.current = setInterval(() => {
      if (cancelledRef.current) return;

      idx += 1;
      if (idx >= validProgression.length) {
        if (loop) {
          idx = 0;
        } else {
          onTogglePlay(false);
          onActiveIndexChange(-1);
          return;
        }
      }
      onActiveIndexChange(idx);
      playArpeggio(validProgression[idx]);
    }, measureDurationMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isPlaying, progression, loop, measureDurationMs, playArpeggio, onTogglePlay, onActiveIndexChange]);

  const handleToggle = () => {
    if (isPlaying) {
      onTogglePlay(false);
      onActiveIndexChange(-1);
    } else {
      onTogglePlay(true);
    }
  };

  const handleReset = () => {
    onTogglePlay(false);
    onActiveIndexChange(-1);
    cancelledRef.current = true;
  };

  return (
    <div className="bg-white/[0.08] rounded-xl border border-white/10 shadow-none px-4 py-3">
      {/* Single row: all controls */}
      <div className="flex items-center gap-3">
        {/* Play / Pause */}
        <button
          onClick={handleToggle}
          className={`w-10 h-10 rounded-full flex items-center justify-center text-white shadow-md transition-colors shrink-0 ${
            isPlaying ? "bg-[var(--bordona-accent-hover)] hover:opacity-90" : "bg-[var(--bordona-accent)] hover:bg-[var(--bordona-accent-hover)]"
          }`}
        >
          {isPlaying ? (
            <Pause className="w-4 h-4" />
          ) : (
            <Play className="w-4 h-4 ml-0.5" />
          )}
        </button>

        {/* Reset */}
        <button
          onClick={handleReset}
          className="w-8 h-8 rounded-full bg-white/[0.08] hover:bg-white/12 flex items-center justify-center text-slate-400 transition-colors shrink-0"
        >
          <RotateCcw className="w-3.5 h-3.5" />
        </button>

        {/* Loop */}
        <button
          onClick={() => setLoop((l) => !l)}
          className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors shrink-0 ${
            loop ? "bg-[var(--bordona-accent-soft)] text-[var(--bordona-accent)]" : "bg-white/[0.07] text-slate-500"
          }`}
        >
          <Repeat className="w-3.5 h-3.5" />
        </button>

        {/* BPM */}
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider">BPM</span>
          <input
            type="range"
            min={40}
            max={200}
            value={bpm}
            onChange={(e) => onBpmChange(Number(e.target.value))}
            className="w-24 h-1 accent-[var(--bordona-accent)]"
          />
          <span className="text-xs font-bold text-slate-100 w-6 text-right">{bpm}</span>
        </div>

        <div className="w-px h-6 bg-white/15 shrink-0" />

        {/* Progress indicator */}
        <div className="flex-1 flex items-center gap-1 min-w-0">
          {progression.map((chord, i) => {
            const funcColor = chord.trim() ? getFunctionColor(chord, keySignature).color : "rgba(255,255,255,0.2)";
            const isActive = i === activeIndex;
            return (
              <div
                key={i}
                className="h-1.5 flex-1 rounded-full transition-all"
                style={{
                  backgroundColor: isActive ? funcColor : chord.trim() ? `${funcColor}55` : "rgba(255,255,255,0.08)",
                  opacity: isActive ? 1 : chord.trim() ? 0.7 : 0.4,
                  transform: isActive ? "scaleY(1.6)" : "scaleY(1)",
                }}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default ProgressionPlayer;
